import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import TrailAnimation from './components/TrailAnimation';
import SuccessMessageAnimation from './components/SuccessMessageAnimation';
import { useSnowReportSignup } from './hooks/useSnowReportSignup';
import Svg from '../../components/react/Svg';

const SnowReportSignup = ({
  endpoint,
  title,
  copy,
  options,
  firstnamePlaceholder,
  lastnamePlaceholder,
  emailPlaceholder,
  buttonLabel,
  successMessage,
  successCopy,
  successLinkLabel,
  firstnameErrorMessage,
  lastnameErrorMessage,
  emailErrorMessage,
  legalCopy
}) => {
  console.log(firstnamePlaceholder);
  const optionsObject = JSON.parse(options);
  const {
    useFetchData,
    handleFirstNameValue,
    handleLastNameValue,
    handleEmailValue,
    handleSelectedOptions,
    handleResetForm,
    isValidFirstName,
    isValidLastName,
    isValidEmail,
    firstName,
    lastName,
    emailAddress,
    isFirstLoad,
    isValid,
    errorMessage,
    isSubmitButtonDisabled,
    subscriptions
  } = useSnowReportSignup({
    endpoint,
    dataTitle: title,
    defaultSubscriptions: optionsObject.map(o => o.value)
  });

  // Handle autofill detection
  useEffect(() => {
    const handleAutofill = () => {
      const inputs = [
        'sign-up-report-first-name',
        'sign-up-report-last-name',
        'sign-up-report-email'
      ];

      inputs.forEach(id => {
        const input = document.getElementById(id);
        if (input && document.activeElement !== input) {
          // Only check autofill if input is not currently focused (user not typing)
          const hasValue = input.value && input.value.length > 0;
          const isAutofilled =
            input.matches(':-webkit-autofill') || input.matches(':autofill');

          // Only apply autofill classes if user hasn't typed yet
          if (isAutofilled && hasValue && !input.dataset.userTyped) {
            input.classList.add('snow-report-signup__input--autofilled');
            input.classList.add('snow-report-signup__input--has-content');
          } else if (input.dataset.userTyped) {
            // User has typed, remove autofill classes and let user input handle styling
            input.classList.remove('snow-report-signup__input--autofilled');
          } else if (!hasValue) {
            // No value and no user input, remove all content classes
            input.classList.remove('snow-report-signup__input--autofilled');
            input.classList.remove('snow-report-signup__input--has-content');
          }
        }
      });
    };

    // Add event listeners to track user typing
    const inputs = [
      'sign-up-report-first-name',
      'sign-up-report-last-name',
      'sign-up-report-email'
    ];

    const handleUserInput = e => {
      // Mark that user has interacted with this input
      e.target.dataset.userTyped = 'true';

      // Silently remove autofill class without visual changes
      e.target.classList.remove('snow-report-signup__input--autofilled');

      // Only manage has-content class when there's actual content change
      if (e.target.value && e.target.value.length > 0) {
        e.target.classList.add('snow-report-signup__input--has-content');
      } else {
        e.target.classList.remove('snow-report-signup__input--has-content');
      }
    };

    const handleFocus = e => {
      // When user focuses on an autofilled field, silently transition to user input mode
      if (
        e.target.classList.contains('snow-report-signup__input--autofilled')
      ) {
        e.target.dataset.userTyped = 'true';
        // Remove autofill class but keep has-content class to maintain styling
        e.target.classList.remove('snow-report-signup__input--autofilled');
        // Don't add has-content class here to avoid visual changes
      }
    };

    inputs.forEach(id => {
      const input = document.getElementById(id);
      if (input) {
        input.addEventListener('input', handleUserInput);
        input.addEventListener('keydown', handleUserInput);
        input.addEventListener('focus', handleFocus);
      }
    });

    // Check on mount and much less frequently
    handleAutofill();
    const interval = setInterval(handleAutofill, 1000);

    // Check on animation frame only once for immediate detection
    requestAnimationFrame(handleAutofill);

    return () => {
      clearInterval(interval);
      inputs.forEach(id => {
        const input = document.getElementById(id);
        if (input) {
          input.removeEventListener('input', handleUserInput);
          input.removeEventListener('keydown', handleUserInput);
          input.removeEventListener('focus', handleFocus);
        }
      });
    };
  }, []);

  return (
    <div className="snow-report-signup__inner">
      <div className="snow-report-signup__container">
        <TrailAnimation isValid={isValid}>
          <h2 className="snow-report-signup__title">{title}</h2>
          <div className="snow-report-signup__copy rtf">{parse(copy)}</div>
          <div className="snow-report-signup__options">
            {optionsObject.map((option, i) => {
              return (
                <label
                  key={i}
                  className="snow-report-signup__input"
                  htmlFor={option.value}
                >
                  <input
                    type="checkbox"
                    name="subscriptions"
                    id={option.value}
                    value={option.value}
                    onChange={() => handleSelectedOptions(option.value)}
                    checked={subscriptions.includes(option.value)}
                    aria-describedby={`${option.value}-description`}
                  />
                  <span className="snow-report-signup__checkmark"></span>
                  <span id={`${option.value}-description`}>{option.label}</span>
                </label>
              );
            })}
          </div>
        </TrailAnimation>
      </div>
      <div className="snow-report-signup__container">
        <TrailAnimation isValid={isValid} delay={250}>
          <div className="snow-report-signup__email-input">
            <input
              type="text"
              name="sign-up-report-first-name"
              id="sign-up-report-first-name"
              onChange={e => handleFirstNameValue(e.target.value)}
              value={firstName}
              className={
                !isValidFirstName && !isFirstLoad
                  ? 'snow-report-signup__email-input--error'
                  : ''
              }
              required
              aria-describedby={
                !isValidFirstName && !isFirstLoad ? 'first-name-error' : null
              }
              aria-invalid={!isValidFirstName && !isFirstLoad}
            />
            <label
              className={
                firstName
                  ? 'snow-report-signup__label snow-report-signup__label--with-text'
                  : 'snow-report-signup__label'
              }
              htmlFor="sign-up-report-first-name"
            >
              {firstnamePlaceholder}
            </label>
            {!isValidFirstName && !isFirstLoad && (
              <p
                id="first-name-error"
                className="snow-report-signup__input-error-message"
                role="alert"
                aria-live="polite"
              >
                {firstnameErrorMessage}
              </p>
            )}
          </div>
          <div className="snow-report-signup__email-input">
            <input
              type="text"
              name="sign-up-report-last-name"
              id="sign-up-report-last-name"
              onChange={e => handleLastNameValue(e.target.value)}
              value={lastName}
              className={
                !isValidLastName && !isFirstLoad
                  ? 'snow-report-signup__email-input--error'
                  : ''
              }
              required
              aria-describedby={
                !isValidLastName && !isFirstLoad ? 'last-name-error' : null
              }
              aria-invalid={!isValidLastName && !isFirstLoad}
            />
            <label
              className={
                lastName
                  ? 'snow-report-signup__label snow-report-signup__label--with-text'
                  : 'snow-report-signup__label'
              }
              htmlFor="sign-up-report-last-name"
            >
              {lastnamePlaceholder}
            </label>
            {!isValidLastName && !isFirstLoad && (
              <p
                id="last-name-error"
                className="snow-report-signup__input-error-message"
                role="alert"
                aria-live="polite"
              >
                {lastnameErrorMessage}
              </p>
            )}
          </div>
          <div className="snow-report-signup__email-input">
            <input
              type="email"
              name="sign-up-report-email"
              id="sign-up-report-email"
              onChange={e => handleEmailValue(e.target.value)}
              value={emailAddress}
              className={
                !isValidEmail && !isFirstLoad
                  ? 'snow-report-signup__email-input--error'
                  : ''
              }
              required
              aria-describedby={
                !isValidEmail && !isFirstLoad ? 'email-error' : null
              }
              aria-invalid={!isValidEmail && !isFirstLoad}
            />
            <label
              className={
                emailAddress
                  ? 'snow-report-signup__label snow-report-signup__label--with-text'
                  : 'snow-report-signup__label'
              }
              htmlFor="sign-up-report-email"
            >
              {emailPlaceholder}
            </label>
            {!isValidEmail && !isFirstLoad && (
              <p
                id="email-error"
                className="snow-report-signup__input-error-message"
                role="alert"
                aria-live="polite"
              >
                {emailErrorMessage}
              </p>
            )}
          </div>
          <div className="snow-report-signup__terms rtf">
            {parse(legalCopy)}
          </div>
          <button
            type="submit"
            className="btn btn--secondary"
            onClick={useFetchData}
            disabled={isSubmitButtonDisabled}
          >
            {buttonLabel}
          </button>
          {!isValid && (
            <p
              className="snow-report-signup__input-error-message"
              role="alert"
              aria-live="polite"
            >
              {errorMessage}
            </p>
          )}
        </TrailAnimation>
      </div>
      <div
        className={
          isValid
            ? 'snow-report-signup__container'
            : 'snow-report-signup__container snow-report-signup__container--zIndex'
        }
      >
        <SuccessMessageAnimation isValid={!isValid}>
          <div className="snow-report-signup__icon-container">
            <Svg
              name="check-icon-circle"
              className="snow-report-signup__svg"
              altText="success"
            />
          </div>
          <h2 className="snow-report-signup__success-title">
            {successMessage}
          </h2>
          <p className="snow-report-signup__success-copy">{successCopy}</p>
          <button
            className="btn btn-outline snow-report-signup__link"
            onClick={() => handleResetForm()}
          >
            {successLinkLabel}
          </button>
        </SuccessMessageAnimation>
      </div>
    </div>
  );
};

SnowReportSignup.propTypes = {
  endpoint: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired,
  options: PropTypes.string.isRequired,
  firstnamePlaceholder: PropTypes.string.isRequired,
  lastnamePlaceholder: PropTypes.string.isRequired,
  emailPlaceholder: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  successMessage: PropTypes.string.isRequired,
  successCopy: PropTypes.string.isRequired,
  successLinkLabel: PropTypes.string.isRequired,
  firstnameErrorMessage: PropTypes.string.isRequired,
  lastnameErrorMessage: PropTypes.string.isRequired,
  emailErrorMessage: PropTypes.string.isRequired,
  legalCopy: PropTypes.string.isRequired
};

export default SnowReportSignup;
